import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toString as qrToSvgString } from "qrcode";
import { KeyRound, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthCard } from "@/components/shell/auth-card";
import { CopyableReference } from "@/components/ui/copyable-reference";
import {
  challengeRecoveryCode,
  challengeTotp,
  confirmTotpEnrolment,
  passkeyAssertionOptions,
  passkeyRegistrationOptions,
  registerPasskey,
  skipMfaEnrolment,
  startTotpEnrolment,
  verifyPasskeyAssertion,
  type MfaChallengePassed,
  type MfaFailure,
  type MfaResult,
} from "@/lib/mfa-api";
import {
  assertionToJson,
  passkeysSupported,
  registrationToJson,
  toCreationOptions,
  toRequestOptions,
} from "@/lib/webauthn";
import { useAuthStore } from "@/stores/auth-store";

/**
 * Alloy-native 2FA page (ALL-5747, specs/alloy-mfa S-4..S-6, S-13 and the UX
 * pass; page spec `auth-mfa`).
 *
 * Rendered by RequireAuth in place of every route while the auth step is
 * `mfa` (GET /auth/session reported ENROLMENT_REQUIRED or CHALLENGE_REQUIRED,
 * or the person chose "Set up" on the ENROLMENT_SUGGESTED prompt). While it is
 * shown the only calls are the `/v1/mfa/**` routes, GET /auth/session after a
 * passed step, and POST /auth/logout.
 *
 * The TOTP secret and the recovery codes live only in this component's state:
 * never in the store, storage or a URL, and they are dropped when the view
 * moves on and when the page unmounts.
 */

export const MFA_COPY = {
  loading: "Preparing your second step",
  enrolHeading: "Protect your Alloy account",
  enrolBody: (provider: string) =>
    `Alloy asks for a second step after you sign in with ${provider}. Set up a passkey to continue.`,
  setUpPasskey: "Set up a passkey",
  useAuthenticatorInstead: "Use an authenticator app instead",
  usePasskeyInstead: "Use a passkey instead",
  notNow: "Not now",
  passkeyPrompt: "Follow your browser's prompt to use your passkey.",
  totpInstructions: (key: string) => `Scan this code with your authenticator app, or enter the key: ${key}`,
  qrAlt: "QR code for your authenticator app",
  codeLabel: "6-digit code",
  verifyAndContinue: "Verify and continue",
  verifying: "Checking…",
  recoveryHeading: "Save your recovery codes",
  recoveryBody: "Each code works once if you lose your passkey or phone.",
  copyCodes: "Copy codes",
  copied: "Copied",
  downloadCodes: "Download codes",
  savedCheckbox: "I saved these codes",
  continue: "Continue",
  challengeHeading: "Confirm it's you",
  challengeBody: (provider: string) => `Alloy asks for a second step after you sign in with ${provider}.`,
  usePasskey: "Use your passkey",
  useAuthenticatorCode: "Use a code from your authenticator app",
  useRecoveryCode: "Use a recovery code",
  recoveryCodeLabel: "Recovery code",
  otherMethod: "Use another method",
  codeInvalid: "That code didn't work. Check the time on your phone and try again.",
  passkeyInvalid: "Your passkey wasn't accepted. Try again or use another method.",
  passkeyCancelled: "The passkey prompt was closed before it finished. Try again.",
  passkeyUnsupported: "This browser can't use passkeys. Use an authenticator app instead.",
  attemptsRemaining: (n: number) => (n === 1 ? "1 attempt remaining." : `${n} attempts remaining.`),
  locked: "Too many attempts. Sign in again in 15 minutes.",
  backToSignIn: "Back to sign in",
  error: "We couldn't complete this step. Try again.",
  retry: "Try again",
  signOut: "Sign out",
} as const;

type View =
  | "enrol-choose"
  | "enrol-passkey"
  | "enrol-totp"
  | "recovery-codes"
  | "challenge"
  | "challenge-totp"
  | "challenge-recovery"
  | "locked"
  | "error";

type Notice =
  | { kind: "invalid"; method: "code" | "passkey"; attemptsRemaining?: number }
  | { kind: "cancelled" }
  | { kind: "unsupported" };

interface TotpSetup {
  enrolmentId: string;
  otpauthUri: string;
  secretBase32: string;
}

/** A readable name for the identity provider the person signed in with. */
export function providerLabel(identityProviderId: unknown): string {
  if (typeof identityProviderId === "string" && /google/i.test(identityProviderId)) return "Google";
  return "your identity provider";
}

/** The otpauth URI as a QR image: SVG generated locally, shown through an <img>, never injected as HTML. */
function TotpQr({ uri }: { uri: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    qrToSvgString(uri, { type: "svg", margin: 1, width: 176 })
      .then((markup) => {
        if (!cancelled) setSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
    };
  }, [uri]);
  if (!src) return <div className="size-[176px] shrink-0 self-center rounded-lg border border-line bg-bg" aria-hidden />;
  return (
    <img
      src={src}
      alt={MFA_COPY.qrAlt}
      width={176}
      height={176}
      className="size-[176px] shrink-0 self-center rounded-lg border border-line bg-white p-1.5"
    />
  );
}

function CodeForm({
  label,
  numeric,
  busy,
  onSubmit,
}: {
  label: string;
  numeric: boolean;
  busy: boolean;
  onSubmit: (code: string) => void;
}) {
  const [code, setCode] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const value = code.trim();
  const ready = numeric ? /^\d{6}$/.test(value) : value.length >= 8 && value.length <= 64;
  return (
    <form
      className="flex w-full flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready || busy) return;
        setCode("");
        onSubmit(value);
      }}
    >
      <label className="flex flex-col gap-1.5 text-left text-sm font-medium text-ink">
        {label}
        <input
          ref={inputRef}
          value={code}
          onChange={(event) =>
            setCode(numeric ? event.target.value.replace(/\D/g, "").slice(0, 6) : event.target.value.slice(0, 64))
          }
          inputMode={numeric ? "numeric" : "text"}
          autoComplete={numeric ? "one-time-code" : "off"}
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={numeric ? 6 : 64}
          disabled={busy}
          className="h-11 w-full rounded-lg border border-line-strong px-3 text-base tracking-widest text-ink outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
        />
      </label>
      <Button type="submit" className="w-full" disabled={!ready || busy} aria-busy={busy}>
        {busy ? MFA_COPY.verifying : MFA_COPY.verifyAndContinue}
      </Button>
    </form>
  );
}

function NoticeText({ notice }: { notice: Notice | null }) {
  if (!notice) return null;
  let text: string;
  if (notice.kind === "invalid") {
    text = notice.method === "code" ? MFA_COPY.codeInvalid : MFA_COPY.passkeyInvalid;
    if (typeof notice.attemptsRemaining === "number") text = `${text} ${MFA_COPY.attemptsRemaining(notice.attemptsRemaining)}`;
  } else if (notice.kind === "cancelled") {
    text = MFA_COPY.passkeyCancelled;
  } else {
    text = MFA_COPY.passkeyUnsupported;
  }
  return (
    <div
      role="alert"
      className="rounded border border-danger-border bg-danger-soft px-3 py-2.5 text-center text-xs leading-relaxed text-danger"
    >
      {text}
    </div>
  );
}

function isCancelled(error: unknown): boolean {
  const name = error && typeof error === "object" && "name" in error ? (error as { name: unknown }).name : null;
  return name === "NotAllowedError" || name === "AbortError";
}

export default function MfaPage() {
  const navigate = useNavigate();
  const mfaMode = useAuthStore((s) => s.mfaMode);
  const step = useAuthStore((s) => s.step);
  const mfaOptional = useAuthStore((s) => s.mfaOptional);
  const session = useAuthStore((s) => s.session);
  const mfaCompleted = useAuthStore((s) => s.mfaCompleted);
  const mfaEnrolmentSkipped = useAuthStore((s) => s.mfaEnrolmentSkipped);
  const signOut = useAuthStore((s) => s.signOut);
  const goLogin = useAuthStore((s) => s.goLogin);

  const initialView: View = mfaMode === "challenge" ? "challenge" : "enrol-choose";
  const [view, setView] = useState<View>(initialView);
  const [pending, setPending] = useState<"loading" | "verifying" | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [supportReference, setSupportReference] = useState<string | undefined>(undefined);
  // Secrets: component state only, dropped as soon as the view moves on.
  const [totp, setTotp] = useState<TotpSetup | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const provider = providerLabel(session?.identityProviderId);
  const methods = Array.isArray(session?.mfaMethodsEnrolled) ? session.mfaMethodsEnrolled : [];
  const offerPasskey = methods.length === 0 || methods.includes("passkey");
  const offerTotp = methods.length === 0 || methods.includes("totp");

  const leave = useCallback(async () => {
    setTotp(null);
    setRecoveryCodes([]);
    navigate("/login", { replace: true });
    goLogin();
    try {
      await signOut();
    } catch {
      // The logout call failed; the person still leaves the product.
      goLogin();
    }
  }, [goLogin, navigate, signOut]);

  /** A failed step: retry in place, the locked state, the error state, or a session re-check. */
  const fail = useCallback(
    (failure: MfaFailure, retryView: View, method: "code" | "passkey") => {
      if (!mounted.current) return;
      setPending(null);
      if (failure.kind === "invalid") {
        setView(retryView);
        setNotice({ kind: "invalid", method, attemptsRemaining: failure.attemptsRemaining });
        return;
      }
      setNotice(null);
      if (failure.kind === "locked") {
        setTotp(null);
        setRecoveryCodes([]);
        setView("locked");
        return;
      }
      if (failure.kind === "required" || failure.kind === "unauthenticated") {
        // The session moved on (already enrolled, or ended): GET /auth/session decides what shows.
        void mfaCompleted();
        return;
      }
      setSupportReference(failure.supportReference);
      setView("error");
    },
    [mfaCompleted],
  );

  /** A passed step: show first-enrolment recovery codes once, else re-check the session. */
  const passed = useCallback(
    (result: MfaChallengePassed) => {
      if (!mounted.current) return;
      setTotp(null);
      setNotice(null);
      if (result.recoveryCodes.length > 0) {
        setPending(null);
        setSaved(false);
        setCopied(false);
        setRecoveryCodes(result.recoveryCodes);
        setView("recovery-codes");
        return;
      }
      void mfaCompleted();
    },
    [mfaCompleted],
  );

  function settle(result: MfaResult<MfaChallengePassed>, retryView: View, method: "code" | "passkey") {
    if (result.ok) passed(result);
    else fail(result, retryView, method);
  }

  async function chooseTotp() {
    setNotice(null);
    setView("enrol-totp");
    setPending("loading");
    const result = await startTotpEnrolment();
    if (!mounted.current) return;
    if (!result.ok) {
      fail(result, "enrol-choose", "code");
      return;
    }
    setTotp({ enrolmentId: result.enrolmentId, otpauthUri: result.otpauthUri, secretBase32: result.secretBase32 });
    setPending(null);
  }

  async function confirmTotp(code: string) {
    if (!totp) return;
    setNotice(null);
    setPending("verifying");
    settle(await confirmTotpEnrolment(totp.enrolmentId, code), "enrol-totp", "code");
  }

  async function enrolPasskey() {
    setNotice(null);
    if (!passkeysSupported()) {
      setNotice({ kind: "unsupported" });
      return;
    }
    setView("enrol-passkey");
    setPending("loading");
    const options = await passkeyRegistrationOptions();
    if (!mounted.current) return;
    if (!options.ok) {
      fail(options, "enrol-choose", "passkey");
      return;
    }
    let credentialJson: string;
    try {
      setPending("verifying");
      const credential = (await navigator.credentials.create({
        publicKey: toCreationOptions(options.options),
      })) as PublicKeyCredential | null;
      if (!credential) throw new DOMException("no credential", "NotAllowedError");
      credentialJson = registrationToJson(credential);
    } catch (error) {
      if (!mounted.current) return;
      setPending(null);
      setView("enrol-choose");
      setNotice(isCancelled(error) ? { kind: "cancelled" } : { kind: "unsupported" });
      return;
    }
    settle(await registerPasskey(credentialJson), "enrol-choose", "passkey");
  }

  async function challengePasskey() {
    setNotice(null);
    if (!passkeysSupported()) {
      setNotice({ kind: "unsupported" });
      return;
    }
    setPending("loading");
    const options = await passkeyAssertionOptions();
    if (!mounted.current) return;
    if (!options.ok) {
      fail(options, "challenge", "passkey");
      return;
    }
    let credentialJson: string;
    try {
      setPending("verifying");
      const credential = (await navigator.credentials.get({
        publicKey: toRequestOptions(options.options),
      })) as PublicKeyCredential | null;
      if (!credential) throw new DOMException("no credential", "NotAllowedError");
      credentialJson = assertionToJson(credential);
    } catch (error) {
      if (!mounted.current) return;
      setPending(null);
      setNotice(isCancelled(error) ? { kind: "cancelled" } : { kind: "unsupported" });
      return;
    }
    settle(await verifyPasskeyAssertion(credentialJson), "challenge", "passkey");
  }

  async function submitCode(code: string, kind: "totp" | "recovery") {
    setNotice(null);
    setPending("verifying");
    const result = kind === "totp" ? await challengeTotp(code) : await challengeRecoveryCode(code);
    settle(result, kind === "totp" ? "challenge-totp" : "challenge-recovery", "code");
  }

  async function notNow() {
    setNotice(null);
    setPending("verifying");
    const result = await skipMfaEnrolment();
    if (!mounted.current) return;
    if (!result.ok) {
      fail(result, "enrol-choose", "code");
      return;
    }
    setPending(null);
    mfaEnrolmentSkipped();
  }

  function continueAfterCodes() {
    if (!saved) return;
    setRecoveryCodes([]);
    void mfaCompleted();
  }

  async function copyCodes() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      if (mounted.current) setCopied(true);
    } catch {
      if (mounted.current) setCopied(false);
    }
  }

  function downloadCodes() {
    try {
      const blob = new Blob([`Alloy recovery codes\n\n${recoveryCodes.join("\n")}\n`], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "alloy-recovery-codes.txt";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      // Download unavailable in this browser; Copy still works.
    }
  }

  function retry() {
    setSupportReference(undefined);
    setNotice(null);
    setTotp(null);
    setView(initialView);
  }

  // Only a session whose second factor is pending (or offered) is shown this page.
  if (step !== "mfa" || !mfaMode) {
    return <Navigate to={step === "app" ? "/wallets" : "/login"} replace />;
  }

  const busy = pending !== null;
  const heading =
    view === "recovery-codes"
      ? MFA_COPY.recoveryHeading
      : mfaMode === "challenge"
        ? MFA_COPY.challengeHeading
        : MFA_COPY.enrolHeading;
  const dataState =
    pending === "loading"
      ? "loading"
      : pending === "verifying"
        ? "verifying"
        : notice?.kind === "invalid"
          ? "failed"
          : view === "challenge-totp" || view === "challenge-recovery"
            ? "challenge"
            : view;

  return (
    <div
      className="relative flex min-h-screen items-center justify-center bg-bg p-4"
      data-testid="mfa-page"
      data-state={dataState}
    >
      <AuthCard>
        <div className="auth-icon" aria-hidden="true">
          <div className="auth-icon__inner">
            {mfaMode === "challenge" ? <KeyRound size={28} strokeWidth={1.25} /> : <ShieldCheck size={28} strokeWidth={1.25} />}
          </div>
        </div>

        <div className="flex w-full flex-col gap-6">
          <div className="auth-heading">
            <h1 className="auth-title">{heading}</h1>
            {view === "enrol-choose" && <p className="auth-subtitle">{MFA_COPY.enrolBody(provider)}</p>}
            {view === "challenge" && <p className="auth-subtitle">{MFA_COPY.challengeBody(provider)}</p>}
            {view === "recovery-codes" && <p className="auth-subtitle">{MFA_COPY.recoveryBody}</p>}
          </div>

          <NoticeText notice={notice} />

          {pending === "loading" && (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="spinner" role="status" aria-label={MFA_COPY.loading} />
            </div>
          )}

          {view === "enrol-choose" && (
            <div className="flex w-full flex-col items-center gap-3">
              <Button className="w-full" onClick={() => void enrolPasskey()} disabled={busy}>
                {MFA_COPY.setUpPasskey}
              </Button>
              <Button variant="secondary" className="w-full" onClick={() => void chooseTotp()} disabled={busy}>
                {MFA_COPY.useAuthenticatorInstead}
              </Button>
              {mfaOptional && (
                <button
                  type="button"
                  onClick={() => void notNow()}
                  disabled={busy}
                  className="text-xs font-medium text-muted outline-none hover:underline focus-visible:underline"
                >
                  {MFA_COPY.notNow}
                </button>
              )}
            </div>
          )}

          {view === "enrol-passkey" && pending === "verifying" && (
            <p className="text-center text-sm text-muted" role="status">
              {MFA_COPY.passkeyPrompt}
            </p>
          )}

          {view === "enrol-totp" && totp && (
            <div className="flex w-full flex-col gap-4">
              <TotpQr uri={totp.otpauthUri} />
              <p className="break-words text-center text-sm text-muted" data-testid="mfa-totp-key">
                {MFA_COPY.totpInstructions(totp.secretBase32)}
              </p>
              <CodeForm label={MFA_COPY.codeLabel} numeric busy={busy} onSubmit={(code) => void confirmTotp(code)} />
              <button
                type="button"
                onClick={() => {
                  setTotp(null);
                  setNotice(null);
                  setView("enrol-choose");
                }}
                disabled={busy}
                className="text-xs font-medium text-primary outline-none hover:underline"
              >
                {MFA_COPY.usePasskeyInstead}
              </button>
            </div>
          )}

          {view === "recovery-codes" && (
            <div className="flex w-full flex-col gap-4">
              <ol
                className="grid grid-cols-2 gap-2 rounded-lg border border-line bg-surface p-4 font-mono text-sm text-ink"
                data-testid="mfa-recovery-codes"
              >
                {recoveryCodes.map((code) => (
                  <li key={code}>{code}</li>
                ))}
              </ol>
              <div className="flex gap-2">
                <Button variant="secondary" className="flex-1" onClick={() => void copyCodes()}>
                  {copied ? MFA_COPY.copied : MFA_COPY.copyCodes}
                </Button>
                <Button variant="secondary" className="flex-1" onClick={downloadCodes}>
                  {MFA_COPY.downloadCodes}
                </Button>
              </div>
              <label className="flex items-start gap-3 text-left text-sm text-ink">
                <input
                  type="checkbox"
                  checked={saved}
                  onChange={(event) => setSaved(event.target.checked)}
                  className="mt-0.5 size-4 shrink-0 accent-primary"
                />
                <span>{MFA_COPY.savedCheckbox}</span>
              </label>
              <Button className="w-full" onClick={continueAfterCodes} disabled={!saved}>
                {MFA_COPY.continue}
              </Button>
            </div>
          )}

          {view === "challenge" && (
            <div className="flex w-full flex-col items-center gap-3">
              {offerPasskey && (
                <Button className="w-full" onClick={() => void challengePasskey()} disabled={busy}>
                  {MFA_COPY.usePasskey}
                </Button>
              )}
              {offerTotp && (
                <Button
                  variant={offerPasskey ? "secondary" : "primary"}
                  className="w-full"
                  onClick={() => {
                    setNotice(null);
                    setView("challenge-totp");
                  }}
                  disabled={busy}
                >
                  {MFA_COPY.useAuthenticatorCode}
                </Button>
              )}
              <button
                type="button"
                onClick={() => {
                  setNotice(null);
                  setView("challenge-recovery");
                }}
                disabled={busy}
                className="text-xs font-medium text-primary outline-none hover:underline"
              >
                {MFA_COPY.useRecoveryCode}
              </button>
            </div>
          )}

          {(view === "challenge-totp" || view === "challenge-recovery") && (
            <div className="flex w-full flex-col items-center gap-3">
              <CodeForm
                key={view}
                label={view === "challenge-totp" ? MFA_COPY.codeLabel : MFA_COPY.recoveryCodeLabel}
                numeric={view === "challenge-totp"}
                busy={busy}
                onSubmit={(code) => void submitCode(code, view === "challenge-totp" ? "totp" : "recovery")}
              />
              <button
                type="button"
                onClick={() => {
                  setNotice(null);
                  setView("challenge");
                }}
                disabled={busy}
                className="text-xs font-medium text-primary outline-none hover:underline"
              >
                {MFA_COPY.otherMethod}
              </button>
            </div>
          )}

          {view === "locked" && (
            <div className="flex w-full flex-col items-center gap-3">
              <div
                role="alert"
                className="rounded border border-danger-border bg-danger-soft px-3 py-2.5 text-center text-sm leading-relaxed text-danger"
              >
                {MFA_COPY.locked}
              </div>
              <Button className="w-full" onClick={() => void leave()}>
                {MFA_COPY.backToSignIn}
              </Button>
            </div>
          )}

          {view === "error" && (
            <div role="alert" className="flex w-full flex-col items-center gap-3 text-center">
              <p className="text-sm font-semibold text-ink">{MFA_COPY.error}</p>
              <CopyableReference reference={supportReference} compact />
              <Button variant="secondary" onClick={retry}>
                {MFA_COPY.retry}
              </Button>
            </div>
          )}

          {view !== "locked" && view !== "recovery-codes" && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => void leave()}
                disabled={pending === "verifying"}
                className="text-xs font-medium text-muted outline-none hover:underline focus-visible:underline"
              >
                {MFA_COPY.signOut}
              </button>
            </div>
          )}
        </div>
      </AuthCard>
    </div>
  );
}
